/**
 * Admin authentication hook
 * Handles Firebase Auth login/logout and admin claims verification
 */
import { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User,
  getIdTokenResult
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

export interface AdminUser {
  user: User;
  role: 'super_admin' | 'operator';
  isAdmin: boolean;
}

export function useAdminAuth() {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const tokenResult = await getIdTokenResult(user);
          const isAdmin = tokenResult.claims.admin === true;
          const role = tokenResult.claims.role as 'super_admin' | 'operator' || 'operator';

          if (isAdmin) {
            setAdminUser({
              user,
              role,
              isAdmin: true
            });
          } else {
            // User is authenticated but not admin - sign them out
            await signOut(auth);
            setAdminUser(null);
          }
        } catch (err: any) {
          console.error('Error checking admin claims:', err);
          setError(err.message);
          setAdminUser(null);
        }
      } else {
        setAdminUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const tokenResult = await userCredential.user.getIdTokenResult();
      
      if (tokenResult.claims.admin !== true) {
        await signOut(auth);
        throw new Error('Not authorized as admin. Please contact administrator.');
      }

      return userCredential.user;
    } catch (err: any) {
      const errorMessage = err.code === 'auth/user-not-found' 
        ? 'Email not found'
        : err.code === 'auth/wrong-password'
        ? 'Incorrect password'
        : err.code === 'auth/invalid-email'
        ? 'Invalid email address'
        : err.message || 'Login failed';
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setAdminUser(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  return {
    adminUser,
    loading,
    error,
    login,
    logout,
    isAuthenticated: adminUser !== null
  };
}

